import React, { useEffect, useState } from 'react';
import { useReducedMotion } from './motion';

/**
 * What just happened to the action, in a picture.
 *
 * The decision panel already says ALLOW, REVIEW or BLOCK and lists the rules
 * that produced it, which is the right answer for somebody who knows the
 * product. For everybody else the useful question is simpler: does this thing
 * run or not? Three outcomes, one lit, in plain words.
 *
 * This is laid out as ordinary elements rather than as an SVG with hand-placed
 * coordinates. The first version drew each box as a fixed-width `<rect>` with
 * the label centred inside it, which meant the box had to be wide enough for a
 * name whose width nobody had measured — so `disable_audit_logging` rendered
 * past both ends of its own box. A box that wraps its own content cannot be
 * outgrown; the only geometry left in SVG is the branch, which has no text in
 * it.
 */

type Decision = 'ALLOW' | 'REVIEW' | 'BLOCK';

const OUTCOMES: { key: Decision; label: string; sub: string }[] = [
  { key: 'ALLOW', label: 'It runs', sub: 'no person needed' },
  { key: 'REVIEW', label: 'It waits for a person', sub: 'held, not refused' },
  { key: 'BLOCK', label: 'It never runs', sub: 'stopped at the boundary' }
];

const TONE: Record<Decision, { chip: string; label: string; dot: string; line: string }> = {
  ALLOW: {
    chip: 'border-emerald-300 bg-emerald-50 dark:border-emerald-400/50 dark:bg-emerald-500/15',
    label: 'text-emerald-800 dark:text-emerald-200',
    dot: 'bg-emerald-500 dark:bg-emerald-400',
    line: 'stroke-emerald-500 dark:stroke-emerald-400'
  },
  REVIEW: {
    chip: 'border-amber-300 bg-amber-50 dark:border-amber-400/50 dark:bg-amber-500/15',
    label: 'text-amber-800 dark:text-amber-200',
    dot: 'bg-amber-500 dark:bg-amber-400',
    line: 'stroke-amber-500 dark:stroke-amber-400'
  },
  BLOCK: {
    chip: 'border-rose-300 bg-rose-50 dark:border-rose-400/50 dark:bg-rose-500/15',
    label: 'text-rose-800 dark:text-rose-200',
    dot: 'bg-rose-500 dark:bg-rose-400',
    line: 'stroke-rose-500 dark:stroke-rose-400'
  }
};

const IDLE_CHIP = 'border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/5';
const IDLE_LINE = 'stroke-slate-300 dark:stroke-white/25';

/**
 * The three-way split between the gate and the outcomes. It stretches to
 * whatever height the outcome column ends up being, which is why it is drawn
 * with `preserveAspectRatio="none"` and a stroke that refuses to scale with it.
 */
const Branch: React.FC<{ decision: Decision; drawn: boolean }> = ({ decision, drawn }) => (
  <div className="relative hidden w-7 self-stretch sm:block" aria-hidden="true">
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 28 100" preserveAspectRatio="none">
      {[17, 50, 83].map((y, index) => {
        const live = OUTCOMES[index].key === decision;
        return (
          <path
            key={y}
            d={`M0 50 C 16 50, 12 ${y}, 28 ${y}`}
            fill="none"
            vectorEffect="non-scaling-stroke"
            strokeWidth={live ? 2 : 1.25}
            opacity={live ? 1 : 0.45}
            className={live ? TONE[decision].line : IDLE_LINE}
            // The lit path draws from the gate outwards. The other two stay
            // dashed, so what did not happen is still legible. `pathLength`
            // normalises the curve to 100 units, which means one dash figure
            // works whatever height the column has stretched the svg to.
            pathLength={100}
            style={live
              ? { strokeDasharray: 100, strokeDashoffset: drawn ? 0 : 100, transition: 'stroke-dashoffset 620ms cubic-bezier(0.22, 0.61, 0.36, 1)' }
              : { strokeDasharray: '4 4' }}
          />
        );
      })}
    </svg>
  </div>
);

const Caption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
    {children}
  </p>
);

export const DecisionFlow: React.FC<{ decision: Decision; action: string }> = ({ decision, action }) => {
  // The verdict is the moment this whole product exists for, and it used to
  // simply be there. Now the lit path draws itself from the gate to the outcome
  // it reached, and the chip it lands on settles into place — so a reader sees
  // *which way* the decision went rather than finding it already decided.
  const reduced = useReducedMotion();
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    if (reduced) { setDrawn(true); return; }
    setDrawn(false);
    // Two frames: one to paint the undrawn state, one to start the transition
    // from it. Setting both in the same frame transitions from nothing.
    let second = 0;
    const first = requestAnimationFrame(() => { second = requestAnimationFrame(() => setDrawn(true)); });
    return () => { cancelAnimationFrame(first); cancelAnimationFrame(second); };
  }, [decision, action, reduced]);

  const summary = decision === 'ALLOW'
    ? `${action} runs, because the approved profile allows it.`
    : decision === 'REVIEW'
      ? `${action} is held until a person authorizes it. It has not been refused.`
      : `${action} does not run. The profile forbids it.`;

  return (
    <figure className="m-0" role="group" aria-label={summary}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2">

        {/* The action. It can shrink and, past that, truncate — the box is never
            the thing that gives way. */}
        <div className="min-w-0 shrink sm:basis-[34%]">
          <Caption>The action</Caption>
          <div className="rounded-xl border border-slate-300 bg-slate-100 px-3 py-2 dark:border-white/15 dark:bg-white/5">
            <code className="block truncate font-mono text-[11px] leading-5 text-slate-700 dark:text-slate-200">
              {action}
            </code>
          </div>
        </div>

        {/* Gate */}
        <div className="min-w-0 shrink-0 sm:basis-[26%]">
          <Caption>Passes through</Caption>
          <div className="rounded-xl border border-brand-300 bg-brand-50 px-3 py-2 text-center dark:border-brand-400/50 dark:bg-brand-500/15">
            <p className="text-[12px] font-bold leading-4 text-brand-800 dark:text-brand-200">Safety gate</p>
            <p className="text-[10px] leading-4 text-slate-500 dark:text-slate-400">approved profile</p>
          </div>
        </div>

        <Branch decision={decision} drawn={drawn} />

        {/* The three outcomes, one lit. */}
        <div className="min-w-0 flex-1">
          <Caption>One of three things happens</Caption>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {OUTCOMES.map((outcome) => {
              const live = outcome.key === decision;
              const tone = TONE[outcome.key];
              return (
                <li
                  key={outcome.key}
                  className={`flex items-start gap-2 rounded-xl border px-2.5 py-1.5 ${live ? tone.chip : IDLE_CHIP}`}
                  style={{
                    opacity: live ? (drawn ? 1 : 0.25) : 0.45,
                    transform: live && !drawn ? 'translateX(-6px)' : 'none',
                    // Behind the line that reaches it, not with it.
                    transition: 'opacity 420ms ease-out 260ms, transform 420ms cubic-bezier(0.22, 0.61, 0.36, 1) 260ms, background-color 300ms, border-color 300ms'
                  }}
                >
                  <span
                    className={`mt-1 h-2 w-2 flex-none rounded-full ${live ? tone.dot : 'bg-slate-300 dark:bg-white/25'}`}
                  />
                  <span className="min-w-0">
                    <span className={`block text-[12px] font-bold leading-4 ${live ? tone.label : 'text-slate-500 dark:text-slate-400'}`}>
                      {outcome.label}
                    </span>
                    <span className="block text-[10px] leading-4 text-slate-500 dark:text-slate-400">
                      {outcome.sub}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <figcaption className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{summary}</figcaption>
    </figure>
  );
};
